import { useContext, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Grid } from "@mui/material";
import { TailSpin } from "react-loader-spinner";
import { useTranslation } from "react-i18next";
import { Asset, ImageType } from "src/business/restaurant/gallery/asset";
import { SnackBar, TipsCard } from "src/design";
import { useGetRestaurantGallery } from "src/data/restaurant/queries/restaurant";
import { useSyncGallery, UploadEntry } from "src/data/restaurant/gallery";
import { RestaurantContext } from "src/Utils/Context/RestaurantContext";
import { UserContext } from "src/Utils/Context/UserContext";
import { isFeatureSectionEnabled } from "src/helpers/FeatureCapabilityConfig";
import { GalleryHeader } from "./components/GalleryHeader";
import { CoverPhotoSection } from "./components/CoverPhotoSection";
import { GalleryTabsSection } from "./components/GalleryTabsSection";
import { GalleryItem } from "./types";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const assetToItem = (a: Asset): GalleryItem => ({ uri: a.uri, url: a.url });

function filterByType(assets: Asset[], type: ImageType): GalleryItem[] {
  return assets.filter((a) => a.imageType === type).map(assetToItem);
}

interface EditState {
  cover: GalleryItem[];
  gallery: GalleryItem[];
  menu: GalleryItem[];
}

// ─── Component ────────────────────────────────────────────────────────────────

export const RestaurantGallery = () => {
  const { t } = useTranslation();
  const { restaurantId } = useParams();
  const navigate = useNavigate();
  const { restaurants } = useContext(RestaurantContext);
  const { user } = useContext(UserContext);

  const canEditGallery = isFeatureSectionEnabled(
    ["gallery_update"],
    "image_gallery_management",
    user,
    restaurants
      .find((r) => r.id === restaurantId)
      ?.capabilities.map((c) => c.name),
  );

  const { images, loading, getRestaurantGallery } =
    useGetRestaurantGallery(restaurantId);
  const { syncing, syncSuccess, syncError, syncGallery, resetSyncStatus } =
    useSyncGallery(restaurantId);

  // ── Edit mode state ──────────────────────────────────────────────────────────
  const [isEditing, setIsEditing] = useState(false);
  const [hasChanged, setHasChanged] = useState(false);
  const [editState, setEditState] = useState<EditState>({
    cover: [],
    gallery: [],
    menu: [],
  });
  const [pendingDeletes, setPendingDeletes] = useState<string[]>([]);

  // Keep a stable view of the last fetched data (used for display when not editing)
  const [viewState, setViewState] = useState<EditState>({
    cover: [],
    gallery: [],
    menu: [],
  });

  useEffect(() => {
    if (!loading && images.length >= 0) {
      setViewState({
        cover: filterByType(images, ImageType.COVER),
        gallery: filterByType(images, ImageType.GALLERY),
        menu: filterByType(images, ImageType.MENU),
      });
    }
  }, [images, loading]);

  useEffect(() => {
    setIsEditing(false);
  }, [restaurantId]);

  // ── Navigation guard ─────────────────────────────────────────────────────────
  if (!restaurantId) {
    navigate(
      restaurants[0]
        ? `/restaurant/${restaurants[0].id}`
        : "/restaurant/no-restaurant",
      { replace: true },
    );
    return null;
  }

  // ── Edit lifecycle ───────────────────────────────────────────────────────────
  const startEditing = () => {
    setEditState({ ...viewState });
    setPendingDeletes([]);
    setHasChanged(false);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setHasChanged(false);
    setPendingDeletes([]);
  };

  const handleSave = async () => {
    const all = [
      ...editState.cover.map((i) => ({ item: i, type: ImageType.COVER })),
      ...editState.gallery.map((i) => ({ item: i, type: ImageType.GALLERY })),
      ...editState.menu.map((i) => ({ item: i, type: ImageType.MENU })),
    ];

    const toUpload: UploadEntry[] = all
      .filter(({ item }) => item.isPending && item.file)
      .map(({ item, type }) => ({ file: item.file!, imageType: type }));

    await syncGallery(toUpload, pendingDeletes);
    getRestaurantGallery();
    setIsEditing(false);
    setHasChanged(false);
    setPendingDeletes([]);
  };

  // ── Mutation helpers ─────────────────────────────────────────────────────────
  const markChanged = () => {
    if (!hasChanged) setHasChanged(true);
  };

  const addToSection = (key: keyof EditState, items: GalleryItem[]) => {
    setEditState((prev) => ({ ...prev, [key]: [...prev[key], ...items] }));
    markChanged();
  };

  const removeFromSection = (key: keyof EditState, item: GalleryItem) => {
    if (item.uri) {
      setPendingDeletes((prev) => [...prev, item.uri!]);
    }
    setEditState((prev) => ({
      ...prev,
      [key]: prev[key].filter((i) => i !== item),
    }));
    markChanged();
  };

  const replaceCover = (item: GalleryItem) => {
    // Mark existing cover for deletion if it exists on the server
    const existing = editState.cover.find((c) => c.uri);
    if (existing?.uri) {
      setPendingDeletes((prev) => [...prev, existing.uri!]);
    }
    setEditState((prev) => ({ ...prev, cover: [item] }));
    markChanged();
  };

  // ── Derived display values ───────────────────────────────────────────────────
  const displayed = isEditing ? editState : viewState;

  // ── Loading ──────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box
        sx={{ display: "flex", width: "100%", justifyContent: "center", mt: 3 }}
      >
        <TailSpin color="#00BFFF" height={80} width={80} />
      </Box>
    );
  }

  return (
    <Grid container justifyContent="center">
      <Grid
        item
        xs={12}
        style={{ maxWidth: "72rem" }}
        sx={{ width: "100%", px: { xs: 2, md: 4 }, py: 3 }}
      >
        <Box>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <GalleryHeader
                isEditing={isEditing}
                hasChanged={hasChanged}
                updating={syncing}
                canEdit={canEditGallery}
                onCancel={handleCancel}
                onEdit={startEditing}
                onSave={handleSave}
              />
            </Grid>

            {/* Cover Photo */}
            <Grid item xs={12}>
              <CoverPhotoSection
                cover={displayed.cover[0] ?? null}
                isEditing={isEditing}
                onReplace={replaceCover}
              />
            </Grid>

            <Grid item xs={12}>
              <Box
                sx={{
                  backgroundColor: "#fff",
                  border: "1px solid #E5E7EB",
                  borderRadius: "12px",
                  boxShadow: "0px 1px 3px rgba(0,0,0,0.08)",
                  overflow: "hidden",
                  px: 3,
                  pt: 2,
                  pb: 2.5,
                }}
              >
                <GalleryTabsSection
                  galleryImages={displayed.gallery}
                  menuImages={displayed.menu}
                  isEditing={isEditing}
                  onDeleteGallery={(item) => removeFromSection("gallery", item)}
                  onAddGallery={(items) => addToSection("gallery", items)}
                  onDeleteMenu={(item) => removeFromSection("menu", item)}
                  onAddMenu={(items) => addToSection("menu", items)}
                />
              </Box>
            </Grid>

            <Grid item xs={12}>
              <TipsCard
                title={t("gallery.tips.title")}
                tips={[
                  t("gallery.tips.quality"),
                  t("gallery.tips.ambiance"),
                  t("gallery.tips.seasonal"),
                  t("gallery.tips.people"),
                ]}
              />
            </Grid>
          </Grid>
        </Box>

        <SnackBar
          onClose={resetSyncStatus}
          open={syncSuccess}
          message={t("gallery.saveSuccess")}
          severity="success"
        />
        <SnackBar
          onClose={resetSyncStatus}
          open={syncError}
          message={t("gallery.saveError")}
          severity="error"
        />
      </Grid>
    </Grid>
  );
};
